import * as Sentry from "@sentry/react";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Scripts,
  useRouter,
} from "@tanstack/react-router";
import { PostHogProvider } from "posthog-js/react";
import { getLangFromCookie } from "@/api/language/language.query.options";
import { CompleteProfileSheet } from "@/components/business/auth/CompleteProfileSheet";
import { Footer } from "@/components/layout/Footer";
import { GlobalOrderListener } from "@/components/order-tracking/GlobalOrderListener";
import { OrderTrackingBubble } from "@/components/order-tracking/OrderTrackingBubble";
import { NotFound } from "@/components/systems/NotFound";
import { SentryErrorFallback } from "@/components/systems/SentryErrorFallback";
import { Toaster } from "@/components/ui/sonner";
import { useLanguage } from "@/hooks/useLanguage";
import { useTheme } from "@/hooks/useTheme";
import i18n from "@/i18n";
import "../lib/posthog";
import { posthog } from "../lib/posthog";
import "../lib/sentry";
import appCss from "../styles.css?url";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()(
  {
    beforeLoad: async () => {
      if (typeof document === "undefined") {
        const lang = await getLangFromCookie();
        if (i18n.language !== lang) await i18n.changeLanguage(lang);
      }
    },
    head: () => ({
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        { title: "Order Online" },
        { name: "description", content: "Order your favourite food online" },
      ],
      links: [
        { rel: "preconnect", href: "https://fonts.googleapis.com" },
        {
          rel: "preconnect",
          href: "https://fonts.gstatic.com",
          crossOrigin: "anonymous",
        },
        {
          rel: "stylesheet",
          href: "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&display=swap",
        },
        { rel: "stylesheet", href: appCss },
        { rel: "icon", href: "/favicon.ico", sizes: "48x48" },
        { rel: "icon", type: "image/svg+xml", href: "/icon.svg" },
      ],
    }),

    notFoundComponent: NotFound,
    shellComponent: RootDocument,
  },
);

function RootDocument({ children }: { children: React.ReactNode }) {
  const { language } = useLanguage();
  const { queryClient } = useRouter().options.context;

  useTheme();

  return (
    <html lang={language} suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body
        className="min-h-screen bg-background text-foreground"
        suppressHydrationWarning
      >
        <PostHogProvider client={posthog}>
          <Sentry.ErrorBoundary
            fallback={<SentryErrorFallback />}
            showDialog={false}
          >
            <QueryClientProvider client={queryClient}>
              <GlobalOrderListener />
              <main className="w-full">{children}</main>
              <Footer />
              <Toaster closeButton />
              <CompleteProfileSheet />
              <OrderTrackingBubble />
            </QueryClientProvider>
          </Sentry.ErrorBoundary>
        </PostHogProvider>
        <Scripts />
      </body>
    </html>
  );
}
